import * as React from 'react';
import ChartAverageInterface from '@app/Interface/Chart/ChartAverageInterface';
import { Chart, ChartArea, ChartAxis, ChartGroup, ChartLabel, ChartLegend, ChartLine, ChartThemeColor, ChartVoronoiContainer } from '@patternfly/react-charts';
import chart_color_black_200 from '@patternfly/react-tokens/dist/esm/chart_color_black_200';
import { Text, TextVariants } from '@patternfly/react-core';
import UserChartNotificationSettingsInterface from '@app/Interface/UserChartNotificationSettingsInterface';
import { DomainPaddingPropType } from 'victory-core';
import moment from 'moment';

interface NDALineChartProps {
    data: ChartAverageInterface[];
    chartType: string;
    hasBaseline: boolean;
    hasNotificationThreshold: boolean;
    userNotificationThreshold: UserChartNotificationSettingsInterface|undefined;
    baselineCaption: string;
}

const NDALineChart: React.FC<NDALineChartProps> = ({data, chartType, hasBaseline, baselineCaption, hasNotificationThreshold, userNotificationThreshold, children}) => {
    const legendData: any[] = [{ childName: 'Producer', name: 'Producer' }, { childName: 'NDA', name: 'NDA'}];
    
    if (hasBaseline) {
        legendData.push({ childName: 'Baseline', name: 'Baseline', symbol: { type: 'dash' } });
    }

    if (hasNotificationThreshold) {
        legendData.push({ childName: 'NotificationThreshold', name: 'Notify', symbol: { fill: chart_color_black_200.value } });
    }

    // Creates the notification threshold ChartArea(s) to display, based on the user's configured notifications
    const getChartNotificationAreas = () => {
        if (!hasNotificationThreshold) {
            return null;
        }
        let yMin1, yMax1, yMin2, yMax2;
        const areas: React.ReactElement[] = [];
        if (userNotificationThreshold?.threshold_range === 'above') {
            const maxYValue = Math.max(...data.map(point => Math.max(point.ndaValue, point.producerValue, point.baseline)));
            yMin1 = () => userNotificationThreshold.threshold_value_min;
            yMax1 = () => maxYValue > userNotificationThreshold.threshold_value_min
                            ? maxYValue
                            : userNotificationThreshold.threshold_value_min + 1;
            areas.push(
                <ChartArea 
                    key="AboveNotificationThreshold"
                    data={data}
                    scale={{x: 'time', y: 'linear'}}
                    x="date"
                    style={{
                        data: {fill: chart_color_black_200},
                    }}
                    y0={yMax1}
                    y={yMin1}
                    name="NotificationThreshold"
                />
            );
        } else if (userNotificationThreshold?.threshold_range === 'below') {
            const minYValue = Math.min(...data.map(point => Math.min(point.ndaValue, point.producerValue, point.baseline)));
            yMin1 = () => minYValue < userNotificationThreshold.threshold_value_max
                            ? minYValue
                            : userNotificationThreshold.threshold_value_max - 1;
            yMax1 = () => userNotificationThreshold.threshold_value_max;
            areas.push(
                <ChartArea 
                    key="BelowNotificationThreshold"
                    data={data}
                    scale={{x: 'time', y: 'linear'}}
                    x="date"
                    style={{
                        data: {fill: chart_color_black_200},
                    }}
                    y0={yMax1}
                    y={yMin1}
                    name="NotificationThreshold"
                />
            );
        } else if (userNotificationThreshold?.threshold_range === 'between') {
            yMin1 = () => userNotificationThreshold.threshold_value_min;
            yMax1 = () => userNotificationThreshold.threshold_value_max;
            areas.push(
                <ChartArea 
                    key="BetweenNotificationThreshold"
                    data={data}
                    scale={{x: 'time', y: 'linear'}}
                    x="date"
                    style={{
                        data: {fill: chart_color_black_200},
                    }}
                    y0={yMax1}
                    y={yMin1}
                    name="NotificationThreshold"
                />
            );
        } else if (userNotificationThreshold?.threshold_range === 'outside') {
            const allMaxYValues: number[] = [];
            const allMinYValues: number[] = [];
            data.forEach(point => {
                allMaxYValues.push(Math.max(point.ndaValue, point.producerValue, point.baseline));
                allMinYValues.push(Math.min(point.ndaValue, point.producerValue, point.baseline));
            });
            const maxYValue = Math.max(...allMaxYValues);
            const minYValue = Math.min(...allMinYValues);
            yMin1 = () => minYValue;
            yMax1 = () => userNotificationThreshold.threshold_value_min;
            yMin2 = () => userNotificationThreshold.threshold_value_max;
            yMax2 = () => maxYValue;
            areas.push(
                <ChartArea 
                    key="OutsideLowerNotificationThreshold"
                    data={data}
                    scale={{x: 'time', y: 'linear'}}
                    x="date"
                    style={{
                        data: {fill: chart_color_black_200},
                    }}
                    y0={yMax1}
                    y={yMin1}
                    name="NotificationThreshold"
                />
            );

            areas.push(
                <ChartArea 
                    key="OutsideUpperNotificationThreshold"
                    data={data}
                    scale={{x: 'time', y: 'linear'}}
                    x="date"
                    style={{
                        data: {fill: chart_color_black_200},
                    }}
                    y0={yMax2}
                    y={yMin2}
                    name="NotificationThreshold"
                />
            );
        }

        return areas;
    };

    // Gets a basic description of the line chart based on the chartType prop
    const getChartDescription = () => {
        let description = 'NDA trend line chart';

        switch (chartType) {
            case 'ash': 
                description = 'Ash trend line chart';
                break;
            case 'butterfat':  
                description = 'Butterfat trend line chart';
                break;
            case 'pre-incubation':  
                description = 'Pre-Incubation trend line chart';
                break;
            case 'protein':  
                description = 'Protein trend line chart';
                break;
            case 'raw':  
                description = 'RAW trend line chart';
                break;
            case 'solid-non-fat':  
                description = 'Solid Non Fat trend line chart';
                break;
            case 'somatic-cell':  
                description = 'Somatic Cell trend line chart';
                break;
            case 'other-solids':  
                description = 'Other Solids trend line chart';
                break;
            case 'mun':  
                description = 'M.U.N. trend line chart';
                break;
        }
        
        return description;
    };

    // Used to get the value to display for a given point's label based on the line the point belongs to
    const getValueForPoint = (point: any) => {
        let value: number|null = null;
        if (point.childName === 'Producer') {
            value = Number(point.producerValue);
        } else if (point.childName === 'NDA') {
            value = Number(point.ndaValue);
        } else {
            value = Number(point.baseline);
        }

        return value === null || Number.isNaN(value)
            ? ''
            : value.toFixed(2);
    };

    return (
        <>
            {!!baselineCaption &&
                <Text className="pf-u-mb-sm pf-u-float-right" component={TextVariants.small}>
                    Plotted Baseline: {baselineCaption}
                </Text>
            }
            <Chart
                ariaDesc={getChartDescription()}
                ariaTitle={getChartDescription()}
                containerComponent={
                    <ChartVoronoiContainer 
                        labels={({ datum }) => {
                            if (datum.childName !== 'NotificationThreshold') {
                                return `${moment(datum.date).format('MMM YY')} \n ${datum.childName}: ${getValueForPoint(datum)}`;
                            }

                            return '';
                        }} 
                        constrainToVisibleArea 
                    />
                }
                legendData={legendData}
                legendPosition="bottom"
                legendComponent={<ChartLegend y={275} />}
                padding={{
                    bottom: 75, // Adjusted to accommodate legend
                    left: 50,
                    right: 50,
                    top: 50
                }}
                themeColor={ChartThemeColor.multiUnordered}
            >
                <ChartAxis 
                    style={{tickLabels: {angle: -45}}} 
                    // Only show every other label for space reasons
                    tickCount={data.length/2}
                    // fixLabelOverlap 
                    showGrid 
                    tickFormat={tick => moment(tick).format('MMM YY')} 
                />
                <ChartAxis 
                    dependentAxis 
                    showGrid 
                />
                <ChartGroup>
                    <ChartLine
                        data={data}
                        scale={{x: 'time', y: 'linear'}}
                        x="date"
                        y="producerValue"
                        name="Producer"
                    />
                    <ChartLine
                        data={data}
                        scale={{x: 'time', y: 'linear'}}
                        x="date"
                        y="ndaValue"
                        name="NDA"
                    />
                    {hasBaseline &&
                        <ChartLine
                            data={data}
                            scale={{x: 'time', y: 'linear'}}
                            style={{
                                data: {
                                    strokeDasharray: '5,3'
                                }
                            }}
                            x="date"
                            y="baseline"
                            name="Baseline"
                        />
                    }
                </ChartGroup>
                {hasNotificationThreshold &&
                    <ChartGroup>
                        {getChartNotificationAreas()}
                    </ChartGroup>
                }
            </Chart>
        </>
    );
}

export default NDALineChart;